// src/components/SelectedDiseaseDetail.tsx
// [2026-06-27 デザイン改善] 主表で選択した行の文脈を「常時表示」するバー。
//   目的:
//   1. クリック連動(主表の行 → 下の ICD/実績K/包括点数 が絞られる)の発見性を上げる。
//   2. 副傷病/重症度(14桁)など、従来ホバー(title属性)にしか無くタッチ/投影/スクショで
//      消えていた情報を常時表示にする。
//   3. 選択解除(×)導線を明示。
//   未選択時はプレースホルダで「行をクリックすると…」を案内する。
// [パンくず 2026-09-28 ユーザ指示「操作感をブラッシュアップ」] 行クリックで分岐の無い階層を飛ばして
//   進むようになったので、「どの桁で何を選んだか」を 疾患 › 8桁目 › 手術 › … の順に並べる。
//   各段をクリックすると、その階層の選択をやり直せる (その階層のタブへ戻り、同じ親の兄弟を並べる)。
//   x の桁の選択は、ユーザがその行を自分でクリックしたときだけ残る (dpcChainPatch が飛ばした階層を
//   埋めないため)。その選択も絞り込みに効くので「分岐なし(x)」として段に出す (隠すと見えない絞り込みになる)。
//   段の名前は一度見た行から覚えておく (タブで上の階層へ戻ると、深い階層の名前を持つ行が表に無くなるため)。
import { useFilters, selectedDpcAt, clearDpcFrom, DPC_LEVELS } from "../store";
import { useDiseaseRanking } from "../lib/useDiseaseRanking";
import { DPC_LEVEL_META, dpcLevelMeta, dpcSegment, isNoBranchSegment } from "../lib/dpcLevels";
import type { DiseaseRankingRow } from "@shared/api";

// コード (各階層で切ったもの) → その階層の分岐名。主表の行を見るたびに足していく (セッション中だけ)。
const NAME_CACHE = new Map<string, string>();
function learnNames(rows: DiseaseRankingRow[] | undefined) {
  for (const r of rows ?? []) {
    for (const m of DPC_LEVEL_META) {
      if (r.dpc6.length < m.v) break;
      const nm = m.name(r);
      if (nm) NAME_CACHE.set(r.dpc6.slice(0, m.v), nm);
    }
  }
}

export function SelectedDiseaseDetail() {
  const f = useFilters();
  const { data } = useDiseaseRanking();
  const lvl = f.dpcLevel;
  learnNames(data?.rows);

  // 選択の入っている階層 (浅い順)。最深のコードで行を引いて各段の名前を取る。
  const picked = DPC_LEVELS
    .map((v) => ({ v, code: selectedDpcAt(f, v) }))
    .filter((p): p is { v: typeof p.v; code: string } => !!p.code);
  const deepest = picked.length ? picked[picked.length - 1].code : null;
  // 主表データ(react-query キャッシュ共有なので追加 fetch なし)から、最深の選択の配下の行を 1 つ引く。
  // 表示階層より深い行は無いので、表示階層で切ったコードでも照合する (タブで上の階層へ戻った場合)。
  const row = deepest && data?.rows
    ? (data.rows.find((r) => r.dpc6.startsWith(deepest)) ?? data.rows.find((r) => deepest.startsWith(r.dpc6)) ?? null)
    : null;

  // [2026-09-28 ユーザ指示] 全部解除したら 疾患別 (6桁) のタブへ戻す (サイドバーの「すべてリセット」と同じ)
  const clearAll = () => { clearDpcFrom(6); f.setDpcLevel(6); };
  const goBackTo = (v: number) => {
    clearDpcFrom(v);
    f.setDpcLevel(v);
  };

  if (!deepest) {
    return (
      <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded px-3 py-2 bg-slate-50">
        ▸ 主表の行をクリックすると、<b>次に分岐がある桁</b>へ進みます（分岐しない桁は飛ばします。
        例: くも膜下出血 → 8桁目 JCS → 手術）。同時に下の
        <b> ICD・病名別 / 実績手術・処置Kコード{lvl === 14 ? " / DPC包括点数" : ""}</b>
        {" "}が絞り込まれます。
      </div>
    );
  }

  const steps = picked;
  const nameOf = (v: number, code: string): string => {
    if (v !== 6 && isNoBranchSegment(code, v)) return "分岐なし (x)";
    const nm = (row && row.dpc6.length >= v ? dpcLevelMeta(v).name(row) : "") || NAME_CACHE.get(code) || "";
    return nm || (v === 6 ? code : `${dpcLevelMeta(v).sub}=${dpcSegment(code, v)}`);
  };

  return (
    <div className="border border-emerald-200 rounded px-3 py-2 bg-emerald-50">
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-xs font-semibold text-emerald-800 mr-1">🔗 絞り込み中</span>
        {steps.map((p, i) => {
          const m = dpcLevelMeta(p.v);
          return (
            <span key={p.v} className="inline-flex items-center gap-1">
              {i > 0 && <span className="text-slate-400">›</span>}
              <button
                type="button"
                onClick={() => goBackTo(p.v)}
                title={`${m.desc}\nクリックで「${m.tab}」の選択をやり直す (この段より後ろの絞り込みを解除)`}
                className="inline-flex items-baseline gap-1 rounded border border-emerald-300 bg-white px-1.5 py-0.5 text-xs hover:bg-emerald-100 hover:border-emerald-500"
              >
                <span className="text-[10px] text-slate-500">{m.crumb}</span>
                {p.v === 6 && <span className="font-mono text-[10px] text-slate-400">{p.code}</span>}
                <span className={p.v === 6 ? "font-semibold" : ""}>{nameOf(p.v, p.code)}</span>
              </button>
            </span>
          );
        })}
        {lvl > (steps[steps.length - 1]?.v ?? 6) && (
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <span className="text-slate-400">›</span>
            いま: <b className="text-emerald-800">{dpcLevelMeta(lvl).tab}</b>の一覧
          </span>
        )}
        <button
          onClick={clearAll}
          className="ml-auto text-xs text-slate-500 hover:text-red-600 hover:border-red-300 border border-slate-300 rounded px-2 py-0.5"
        >
          × 解除
        </button>
      </div>
      <div className="text-[11px] text-emerald-700 mt-1">
        ↓ 下の表はこの選択で絞り込み中
        <span className="text-slate-500 ml-2">（段をクリックでその桁から選び直し）</span>
      </div>
    </div>
  );
}
